import { useState, useCallback, useRef } from 'react';
import { MentionConfig, MenuState } from '../types';
import { findMentionTrigger, getCaretCoordinates } from '../utils';

export const useMentionDetection = (
  textareaRef: React.RefObject<HTMLTextAreaElement | null>,
  mentions: MentionConfig[]
) => {
  const [menuState, setMenuState] = useState<MenuState>({
    isOpen: false,
    position: { x: 0, y: 0 },
    currentLayer: 0,
    layerStack: [],
    searchQuery: '',
    selectedIndex: 0,
    triggerChar: '',
    triggerPosition: 0,
    sourcePath: [],
    sourceIds: []
  });

  const triggers = mentions.map(m => m.trigger);

  const detectMention = useCallback((text: string, cursorPosition: number) => {
    if (!textareaRef.current) return;

    const mentionTrigger = findMentionTrigger(text, cursorPosition, triggers);

    if (mentionTrigger) {
      const mentionConfig = mentions.find(m => m.trigger === mentionTrigger.trigger);
      if (!mentionConfig) return;

      const coordinates = getCaretCoordinates(textareaRef.current, mentionTrigger.position);
      const rect = textareaRef.current.getBoundingClientRect();

      setMenuState(prev => ({
        ...prev,
        isOpen: true,
        position: {
          x: rect.left + coordinates.left,
          y: rect.top + coordinates.top + coordinates.height + 4
        },
        currentLayer: 0,
        layerStack: [mentionConfig.data],
        searchQuery: mentionTrigger.query,
        selectedIndex: 0,
        triggerChar: mentionTrigger.trigger,
        triggerPosition: mentionTrigger.position,
        sourcePath: [],
        sourceIds: []
      }));
    } else {
      setMenuState(prev => ({ ...prev, isOpen: false }));
    }
  }, [textareaRef, mentions, triggers]);

  const closeMenu = useCallback(() => {
    setMenuState(prev => ({
      ...prev,
      isOpen: false,
      sourcePath: [],
      sourceIds: [],
      currentLayer: 0,
      layerStack: []
    }));
  }, []);

  const updateSearch = useCallback((query: string) => {
    setMenuState(prev => ({ ...prev, searchQuery: query, selectedIndex: 0 }));
  }, []);

  const navigateToLayer = useCallback((items: any[], sourceItem?: any) => {
    setMenuState(prev => ({
      ...prev,
      currentLayer: prev.currentLayer + 1,
      layerStack: [...prev.layerStack, items],
      searchQuery: '',
      selectedIndex: 0,
      sourcePath: sourceItem ? [...prev.sourcePath, sourceItem.label] : prev.sourcePath,
      sourceIds: sourceItem ? [...prev.sourceIds, sourceItem.id] : prev.sourceIds
    }));
  }, []);

  const navigateBack = useCallback(() => {
    setMenuState(prev => {
      if (prev.currentLayer > 0) {
        const newLayerStack = prev.layerStack.slice(0, -1);
        const newSourcePath = prev.sourcePath.slice(0, -1);
        const newSourceIds = prev.sourceIds.slice(0, -1);
        return {
          ...prev,
          currentLayer: prev.currentLayer - 1,
          layerStack: newLayerStack,
          searchQuery: '',
          selectedIndex: 0,
          sourcePath: newSourcePath,
          sourceIds: newSourceIds
        };
      }
      return prev;
    });
  }, []);

  const setSelectedIndex = useCallback((index: number) => {
    setMenuState(prev => ({ ...prev, selectedIndex: index }));
  }, []);

  return {
    menuState,
    detectMention,
    closeMenu,
    updateSearch,
    navigateToLayer,
    navigateBack,
    setSelectedIndex
  };
};
